import {
  Button,
  Container,
  Grid,
  Stack,
  Typography,
  CircularProgress,
  Tooltip
} from '@mui/material';
import { Box } from '@mui/system';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { BiCloudUpload } from 'react-icons/bi';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import {
  getBankDropdownData,
  getCityData,
  getLoanTypeData,
  saveApiData,
  getStateData,
  setCityData,
  savefileApiData
} from '../../store/Slice/Global/GlobalSlice';
import { PayoutApi } from '../../utils/Apis';
import CustomInput from '../../components/CustomInput';
import CustomSelect from '../../components/CustomSelect';

function AddEditPayout() {
  const { rowId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [sanctionedFile, setSanctionedFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [fileUploaded, setFileUploaded] = useState(false);

  const {
    handleSubmit: handleReduxSubmit,
    control,
    setValue,
    formState: { errors }
  } = useForm({
    mode: 'onBlur', // Add this to validate on blur
    defaultValues: {
      fileNumber: '',
      fileSubmissionDate: '',
      bankId: '',
      stateId: '',
      cityId: '',
      borrowerName: '',
      loanTypeId: '',
      disburseMonth: '',
      disburseAmount: '',
      sanctionedMonth: '',
      sanctionedAmount: '',
      encashmentMonth: '',
      encashmentAmount: ''
    }
  });

  const dispatch = useDispatch();
  const { LoginData } = useLoginSelector();
  const { LoanTypeDrop, BankDrop, CityDrop, stateDrop } = useGlobalSelector();

  const handleSanctionedLetterUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSanctionedFile(file);
    if (!rowId) {
      // In Add mode, just store file locally and upload on submit
      setFileUploaded(true);
      return;
    }
    setUploadingFile(true);
    setFileUploaded(false);
    try {
      const formData = new FormData();
      formData.append('partnerPayoutId', rowId);
      formData.append('companyId', LoginData?.companyId || '');
      formData.append('userId', LoginData?.userId || '');
      formData.append('file', file);
      const res = await dispatch(savefileApiData(PayoutApi?.uploadSanctionedLetter, formData));
      if (res) {
        setFileUploaded(true);
      }
    } catch (error) {
      console.error('Sanctioned letter upload error:', error);
    } finally {
      setUploadingFile(false);
    }
  };

  const onSubmit = useCallback(
    async (data) => {
      const finalData = {
        ...data,
        companyId: LoginData?.companyId,
        userId: LoginData?.userId,
        employeeClass: 'Others',
        partnerId: null,
        applicationId: null,
        sourceId: 314
      };
      try {
        const res = await dispatch(saveApiData(PayoutApi?.addupdatePayout, finalData));
        if (res?.status === 'success') {
          const newPartnerPayoutId = res?.data?.partnerPayoutId || res?.data?.id;
          if (newPartnerPayoutId && sanctionedFile && !rowId) {
            const formData = new FormData();
            formData.append('partnerPayoutId', newPartnerPayoutId);
            formData.append('companyId', LoginData?.companyId || '');
            formData.append('userId', LoginData?.userId || '');
            formData.append('file', sanctionedFile);
            await dispatch(savefileApiData(PayoutApi?.uploadSanctionedLetter, formData));
          }
          navigate('/payout');
        }
      } catch (error) {
        console.error('Error submitting form:', error);
      }
    },
    [LoginData, sanctionedFile, rowId, dispatch, navigate] // 🔹 depends on state/refs
  );

  const getLoanTypeBankDropAPi = useCallback(() => {
    if (!LoginData) return;
    const data = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId
    };
    dispatch(getLoanTypeData(data));
    dispatch(getBankDropdownData(data));
  }, [LoginData]);

  // city api
  const handleGetState = (optionVal) => {
    const data = {
      stateId: optionVal
    };
    dispatch(getCityData(data)); // city data
  };

  // State → City cascading
  const handleStateChange = (stateVal) => {
    dispatch(setCityData([]));
    setValue('cityId', null);
    if (stateVal) dispatch(getCityData({ stateId: stateVal }));
  };

  useEffect(() => {
    getLoanTypeBankDropAPi();
  }, [getLoanTypeBankDropAPi]);

  useEffect(() => {
    dispatch(getStateData({ countryId: 101 })); // load states on mount
  }, []);

  return (
    <Page title="Partner Management System || Payout">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info={rowId ? 'Edit File' : 'Add New File'} />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>
          <Typography variant="body1" className="headingLineUp headingLineUpOut">
            File Details
          </Typography>
          <Box className="viewLeadCard cardTopMinusMargin" mb={3}>
            <form id="addPayout" onSubmit={handleReduxSubmit(onSubmit)}>
              <Grid container spacing={3} mb={3}>
                <Grid item xs={4}>
                  <CustomInput
                    name="fileNumber"
                    label="File No"
                    control={control}
                    errors={errors}
                    isRequired
                    fieldCode="FILE_NO"
                    fieldType="TEXT_FIELD"
                  />
                </Grid>
                <Grid item xs={4}>
                  <CustomInput
                    name="fileSubmissionDate"
                    label="Submission Date"
                    type="date"
                    control={control}
                    errors={errors}
                    isRequired
                    fieldCode="DOB"
                    fieldType="DATE_FIELD"
                  />
                </Grid>
                <Grid item xs={4}>
                  <CustomSelect
                    name="bankId"
                    label="Bank Name"
                    control={control}
                    errors={errors}
                    options={BankDrop || []}
                    isRequired
                  />
                </Grid>
                <Grid item xs={4}>
                  <CustomSelect
                    name="stateId"
                    label="State"
                    control={control}
                    errors={errors}
                    options={stateDrop || []}
                    isRequired
                    onChange={(e, val) => handleStateChange(val)}
                  />
                </Grid>
                <Grid item xs={4}>
                  <CustomSelect
                    name="cityId"
                    label="City"
                    control={control}
                    errors={errors}
                    options={CityDrop || []}
                    isRequired
                  />
                </Grid>
                <Grid item xs={4}>
                  <CustomInput
                    name="borrowerName"
                    label="Borrower Name"
                    control={control}
                    errors={errors}
                    isRequired
                    fieldCode="NAME"
                    fieldType="ALPHABET"
                  />
                </Grid>
                <Grid item xs={4}>
                  <CustomSelect
                    name="loanTypeId"
                    label="Loan Type"
                    control={control}
                    errors={errors}
                    options={LoanTypeDrop || []}
                    isRequired
                  />
                </Grid>
                <Grid item xs={4}>
                  <CustomInput
                    name="disburseMonth"
                    label="Disbursed Month"
                    type="month"
                    control={control}
                    errors={errors}
                    isRequired
                    fieldCode="DATE"
                    fieldType="DATE_FIELD"
                  />
                </Grid>
                <Grid item xs={4}>
                  <CustomInput
                    name="disburseAmount"
                    label="Disbursed Amount"
                    type="Number"
                    control={control}
                    errors={errors}
                    isRequired
                    fieldType="AMOUNT_FIELD"
                  />
                </Grid>
                <Grid item xs={4}>
                  <CustomInput
                    name="sanctionedMonth"
                    label="Sanctioned Month"
                    type="month"
                    control={control}
                    errors={errors}
                    isRequired
                    fieldCode="DATE"
                    fieldType="DATE_FIELD"
                  />
                </Grid>
                <Grid item xs={4}>
                  <CustomInput
                    name="sanctionedAmount"
                    label="Sanctioned Amount"
                    type="Number"
                    control={control}
                    errors={errors}
                    isRequired
                    fieldType="AMOUNT_FIELD"
                  />
                </Grid>
                {/* Sanctioned Letter Doc Upload */}
                <Grid item xs={4}>
                  <Box>
                    <Typography variant="h6" className="formLabel">
                      Add Sanctioned Letter Doc{' '}
                      {/* <span style={{ color: '#888', fontWeight: 400, fontSize: '11px' }}>
                        (Optional)
                      </span> */}
                    </Typography>
                    <Tooltip
                      title={
                        fileUploaded
                          ? `Uploaded: ${sanctionedFile?.name || 'File Selected'}`
                          : 'Click to upload PDF / JPG / PNG'
                      }
                      placement="top"
                      arrow
                    >
                      <Box
                        className="textInput"
                        onClick={() => fileInputRef.current?.click()}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          cursor: 'pointer',
                          border: fileUploaded ? '1px solid #2e7d32' : '1px solid #d0d0d0',
                          borderRadius: '4px',
                          px: 1.5,
                          py: '9px',
                          bgcolor: fileUploaded ? '#f6fff7' : '#fff',
                          minHeight: '40px',
                          transition: 'border-color 0.2s',
                          '&:hover': {
                            borderColor: fileUploaded ? '#2e7d32' : '#888'
                          }
                        }}
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          style={{ display: 'none' }}
                          onChange={handleSanctionedLetterUpload}
                        />
                        {uploadingFile ? (
                          <CircularProgress size={16} sx={{ color: 'primary.main' }} />
                        ) : fileUploaded ? (
                          <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
                        ) : (
                          <BiCloudUpload size={18} style={{ color: '#aaa' }} />
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '13px',
                            color: fileUploaded ? '#2e7d32' : '#999',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1
                          }}
                        >
                          {uploadingFile
                            ? 'Uploading...'
                            : fileUploaded
                            ? sanctionedFile?.name || 'File Selected'
                            : 'Upload document'}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <CustomInput
                    name="encashmentMonth"
                    label="Encashment Month"
                    type="month"
                    control={control}
                    errors={errors}
                    isRequired
                    fieldCode="DATE"
                    fieldType="DATE_FIELD"
                  />
                </Grid>
                <Grid item xs={4}>
                  <CustomInput
                    name="encashmentAmount"
                    label="Encashment Amount"
                    type="Number"
                    control={control}
                    errors={errors}
                    isRequired
                    fieldType="AMOUNT_FIELD"
                  />
                </Grid>
              </Grid>{' '}
            </form>{' '}
          </Box>

          <Box className="" textAlign="right">
            <Button variant="contained" className="mainBtn" type="submit" form="addPayout">
              Submit
            </Button>
          </Box>
        </Box>
      </Container>
    </Page>
  );
}
export default AddEditPayout;
